import {
  IntegrationType,
  SidekickSupportedTms,
} from '@shared/generated/graphql';
import { isNil } from 'lodash';
import { getAljexPageInfo } from './aljex';
import { getLegacyMercuryGatePageInfo } from './legacyMercuryGate';
import { getLoadSmartPageInfo } from './loadsmart';
import { getLoadStopPageInfo } from './loadstop';
import { getMcleodWebPageInfo } from './mcleodWeb';
import {
  getMercuryGatePageInfo,
  getMercuryGateShipmentUrls,
} from './mercuryGate';
import { getTaiPageInfo } from './tai';
import { getThreePLPageInfo } from './threePL';
import { getTurvoPageInfo } from './turvo';
import {
  PageInfo,
  SUPPORTED_TMS_DOMAINS,
  SupportedEntityType,
  TMSPageInfo,
} from './types';
import { getUberGtgPageInfo } from './uberGtg';

export function matchTMS(url: string): SidekickSupportedTms | null {
  const tmsEntry = Object.entries(SUPPORTED_TMS_DOMAINS).find(([_, domain]) =>
    url.includes(domain)
  );
  return tmsEntry ? (tmsEntry[0] as SidekickSupportedTms) : null;
}

export function getTMSPageInfo(
  tms: SidekickSupportedTms,
  url: string,
  domIframe: string,
  dom: string
): TMSPageInfo {
  switch (tms) {
    case SidekickSupportedTms.ALJEX:
      return getAljexPageInfo(dom, domIframe);
    case SidekickSupportedTms.LEGACY_MERCURYGATE:
      return getLegacyMercuryGatePageInfo(dom);
    case SidekickSupportedTms.LOADSMART:
      return getLoadSmartPageInfo(url);
    case SidekickSupportedTms.LOADSTOP:
      return getLoadStopPageInfo(dom, domIframe);
    case SidekickSupportedTms.MCLEOD_WEB:
      return getMcleodWebPageInfo(url);
    case SidekickSupportedTms.MERCURYGATE:
      return getMercuryGatePageInfo(url);
    case SidekickSupportedTms.TAI:
      return getTaiPageInfo(url);
    case SidekickSupportedTms.THREE_PL:
      return getThreePLPageInfo(url);
    case SidekickSupportedTms.TURVO:
      return getTurvoPageInfo(url);
    case SidekickSupportedTms.UBER_GTG:
      return getUberGtgPageInfo(url);
    default:
      return getUnknownTMSPageInfo();
  }
}

export function integrationTypeToTms(
  integrationType: IntegrationType
): SidekickSupportedTms | null {
  switch (integrationType) {
    case IntegrationType.ALJEX:
      return SidekickSupportedTms.ALJEX;
    case IntegrationType.HOSTED_MCLEOD:
      return SidekickSupportedTms.MCLEOD_WEB;
    case IntegrationType.TAI:
      return SidekickSupportedTms.TAI;
    case IntegrationType.THREE_PL:
      return SidekickSupportedTms.THREE_PL;
    case IntegrationType.LEGACY_MERCURYGATE:
      return SidekickSupportedTms.LEGACY_MERCURYGATE;
    case IntegrationType.LOADSMART:
      return SidekickSupportedTms.LOADSMART;
    case IntegrationType.LOADSTOP:
      return SidekickSupportedTms.LOADSTOP;
    case IntegrationType.MERCURYGATE:
      return SidekickSupportedTms.MERCURYGATE;
    case IntegrationType.TURVO:
      return SidekickSupportedTms.TURVO;
    case IntegrationType.UBER_GTG:
      return SidekickSupportedTms.UBER_GTG;
    default:
      return null;
  }
}

export function getTMSShipmentUrls(
  tms: SidekickSupportedTms,
  parentUrl: string,
  referenceNumber: string
): string[] | undefined {
  switch (tms) {
    case SidekickSupportedTms.MERCURYGATE:
      return getMercuryGateShipmentUrls(parentUrl, referenceNumber);
    default:
      return undefined;
  }
}

function getUnknownTMSPageInfo(): TMSPageInfo {
  return { pageType: 'tms', tms: SidekickSupportedTms.UNKNOWN, entityInfo: [] };
}

export function canBuildShipment(pageInfo?: PageInfo | null): boolean {
  if (isNil(pageInfo)) return false;
  if (pageInfo.pageType !== 'tms') return false;
  return pageInfo.entityInfo.some(
    (entityInfo) => entityInfo.entityType === SupportedEntityType.SHIPMENT
  );
}
